import React from 'react';
import {
  FilterBar,
  FilterSelect
} from './FilterBar';
import { Wifi, WifiOff } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SavedSearchesPopover } from './SavedSearchesPopover';

/**
 * Shipment Filter Bar Component
 * Uses shared FilterBar components for consistency
 */
export default function ShipmentFilterBar({
  // Search
  searchTerm,
  onSearchChange,
  searchPlaceholder = 'Search container, MBL, customer...',
  // Filters
  showFilters,
  onToggleFilters,
  statusFilter,
  onStatusFilterChange,
  sslFilter,
  onSslFilterChange,
  driverFilter,
  onDriverFilterChange,
  customerFilter,
  onCustomerFilterChange,
  // Data for filters
  statusOptions = [],
  sslOptions = [],
  driverOptions = [],
  customerOptions = [],
  // Other options
  includeClosed,
  onIncludeClosedChange,
  hiddenStatuses,
  onHiddenStatusesChange,
  // Sort
  sortColumn,
  sortDirection,
  onClearSort,
  // Column settings
  columnsChanged,
  savingColumns,
  onSaveColumns,
  // Actions
  onRefresh,
  onClearFilters,
  // Computed
  hasActiveFilters,
  // WebSocket
  wsConnected = false,
  // Saved searches (optional — falsy disables the feature gracefully)
  savedSearches,
  savedSearchesLoading,
  onApplySavedSearch,
  onSaveCurrentSearch,
  onDeleteSavedSearch,
  // Translations
  t = (key) => key
}) {
  // Render additional filters for expandable section
  const additionalFilters = (
    <>
      {/* Status Filter */}
      <FilterSelect
        value={statusFilter}
        onChange={onStatusFilterChange}
        options={statusOptions}
        placeholder="Filter by Status"
        allLabel="All Statuses"
        className="w-full sm:w-[180px]"
        testId="status-filter"
      />
      
      {/* SSL Filter */}
      <FilterSelect
        value={sslFilter}
        onChange={onSslFilterChange}
        options={sslOptions}
        placeholder="Filter by SSL"
        allLabel="All SSLs"
        className="w-full sm:w-[150px]"
        testId="ssl-filter"
      />
      
      {/* Driver Filter */}
      <FilterSelect
        value={driverFilter}
        onChange={onDriverFilterChange}
        options={driverOptions}
        placeholder="Filter by Driver"
        allLabel="All Drivers"
        className="w-full sm:w-[150px]"
        testId="driver-filter"
      />
      
      {/* Customer Filter */}
      <FilterSelect
        value={customerFilter}
        onChange={onCustomerFilterChange}
        options={customerOptions}
        placeholder="Filter by Customer"
        allLabel="All Customers"
        className="w-full sm:w-[150px]"
        testId="customer-filter"
      />
    </>
  );

  return (
    <FilterBar
      // Search
      searchValue={searchTerm}
      onSearchChange={onSearchChange}
      searchPlaceholder={searchPlaceholder}
      searchTestId="shipment-search-input"
      // Expandable filters
      additionalFilters={additionalFilters}
      showFilters={showFilters}
      onToggleFilters={onToggleFilters}
      hasActiveFilters={hasActiveFilters}
      onClearFilters={onClearFilters}
      // Include closed
      showIncludeClosed={true}
      includeClosed={includeClosed}
      onIncludeClosedChange={onIncludeClosedChange}
      // Hidden statuses
      hiddenStatuses={hiddenStatuses}
      onHiddenStatusesChange={onHiddenStatusesChange}
      // Refresh
      showRefresh={true}
      onRefresh={onRefresh}
      // Sort
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onClearSort={onClearSort}
      // Column settings
      columnsChanged={columnsChanged}
      savingColumns={savingColumns}
      onSaveColumns={onSaveColumns}
      // Custom action - WebSocket status indicator + Saved Searches popover
      customActions={
        <>
          {/* Saved Searches popover — opt-in via parent passing handlers */}
          {onSaveCurrentSearch && (
            <SavedSearchesPopover
              items={savedSearches || []}
              loading={!!savedSearchesLoading}
              hasActiveFilters={hasActiveFilters}
              onApply={onApplySavedSearch}
              onSave={onSaveCurrentSearch}
              onDelete={onDeleteSavedSearch}
            />
          )}
          <Badge 
            className={`flex items-center gap-1 text-xs ${
              wsConnected 
                ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                : 'bg-slate-500/20 text-slate-400 border-slate-500/30'
            }`}
            title={wsConnected ? 'Real-time updates active' : 'Reconnecting...'}
          >
            {wsConnected ? (
              <>
                <Wifi className="h-3 w-3" />
                <span className="hidden sm:inline">Live</span>
              </>
            ) : (
              <>
                <WifiOff className="h-3 w-3" />
                <span className="hidden sm:inline">Offline</span>
              </>
            )}
          </Badge>
        </>
      }
    />
  );
}
